/*
  Aloha Amino — Marketing homepage
  ────────────────────────────────────────────────────────────────────────────
  • Design language emulates shopunrl: airy pale-blue gradient wash, floating
    white rounded cards with soft shadows, bright azure accents and bold black
    headlines with a bright-blue second line, product vials with cyan labels.
  • COMPLIANCE: products are sold for in-vitro/laboratory research only.
    Copy must NOT promise therapeutic effects. Research-only callouts MUST stay.
*/
import { useEffect, useRef, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthCtx } from '../App';
import { useIdleTrigger } from '../hooks/useIdleTrigger';
import SignupPromoModal from '../components/shop/SignupPromoModal';
import Logo from '../components/Logo';
import { productImage, onImageError } from '../utils/helpers';
import { getActiveHero } from '../data/campaigns';

const BRIGHT = '#1FA7E8';       // UNRL-style bright azure accent (testimonial dots, etc.)

const card = 'rounded-3xl bg-white border border-white shadow-[0_14px_44px_rgba(20,120,200,0.10)]';
const btnPrimary =
  'inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 font-semibold text-white text-[15px] ' +
  'bg-[#1FA7E8] hover:bg-[#1391D6] shadow-[0_8px_20px_rgba(31,167,232,0.35)] transition-all hover:-translate-y-0.5 ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1683C7] focus-visible:ring-offset-2';
const btnGhost =
  'inline-flex items-center justify-center gap-2 rounded-full border border-cash-200 bg-white px-7 py-3.5 ' +
  'font-semibold text-cash-700 text-[15px] transition-all hover:border-[#1FA7E8] hover:text-cash-ink ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1683C7]';

const gridTexture = {
  backgroundImage:
    'linear-gradient(rgba(31,167,232,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(31,167,232,0.07) 1px, transparent 1px)',
  backgroundSize: '24px 24px',
};

// Studio product photo — renders the real photoreal catalog shots (pastel
// studio vials) so the homepage matches the products we actually ship. Pass a
// product `slug`; the photo's own backdrop reads as the panel surface. Use
// object-cover to fill square panels/tiles.
function VialPhoto({ slug = 'reta', className = '', style }) {
  return (
    <img
      src={productImage(slug)}
      onError={onImageError}
      alt="Aloha Amino research peptide vial"
      className={className}
      style={style}
      loading="lazy"
    />
  );
}

const ArrowRight = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

// ──────────────────────────────────────────────────────────────────────────
// Header — floating white rounded card (UNRL-style)
// ──────────────────────────────────────────────────────────────────────────
function HomeHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const openerRef = useRef(null);
  const { user, logout } = useAuthCtx();
  const navigate = useNavigate();

  const handleLogout = async () => { await logout(); setMenuOpen(false); navigate('/'); };

  useEffect(() => {
    if (!menuOpen) return;
    const root = menuRef.current;
    const focusables = root.querySelectorAll('a, button, input, [tabindex]:not([tabindex="-1"])');
    focusables[0]?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') { setMenuOpen(false); openerRef.current?.focus(); }
      if (e.key === 'Tab' && focusables.length) {
        const first = focusables[0], last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [menuOpen]);

  const navLinks = [
    { href: '/shop', label: 'Catalog' },
    { href: '/membership', label: 'VIP' },
    { href: '/about', label: 'About' },
    { href: '/coa', label: 'COAs' },
    { href: '/compliance', label: 'Compliance' },
    { href: '/support', label: 'Lab Support' },
  ];

  return (
    <header className="sticky top-0 z-40 px-4 pt-3 md:pt-4">
      <div className="cash-container !px-0">
        <div className="flex h-14 md:h-16 items-center justify-between rounded-2xl bg-white/85 backdrop-blur-md border border-white px-4 md:px-6 shadow-[0_10px_30px_rgba(20,120,200,0.10)]">
          <Link to="/" aria-label="Aloha Amino — home"
            className="flex items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1683C7] rounded-full px-1">
            <Logo className="text-lg md:text-xl" />
          </Link>

          <nav aria-label="Primary" className="hidden md:flex items-center gap-7">
            {navLinks.map((l) => (
              <a key={l.label} href={l.href}
                className="text-cash-700 font-medium text-[15px] hover:text-[#1683C7] transition-colors focus:outline-none focus-visible:underline underline-offset-4">
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            {user ? (
              <>
                <Link to="/account" className="hidden md:inline-flex text-cash-700 hover:text-cash-ink font-medium text-sm mr-1">My account</Link>
                <button onClick={handleLogout} className="hidden md:inline-flex text-cash-500 hover:text-cash-ink font-medium text-sm mr-1">Log out</button>
                <Link to="/shop" className={btnPrimary + ' !px-5 !py-2.5'}>Browse Catalog</Link>
              </>
            ) : (
              <>
                <Link to="/login" className="hidden md:inline-flex text-cash-700 hover:text-cash-ink font-medium text-sm mr-1">Log in</Link>
                <Link to="/shop" className={btnPrimary + ' !px-5 !py-2.5'}>Browse Catalog</Link>
              </>
            )}
            <button ref={openerRef}
              className="md:hidden inline-flex items-center justify-center h-10 w-10 rounded-full border border-cash-200 text-cash-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1683C7]"
              aria-label="Open menu" aria-expanded={menuOpen} aria-controls="mobile-menu" onClick={() => setMenuOpen(true)}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M4 7h16M4 12h16M4 17h16" /></svg>
            </button>
          </div>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden fixed inset-0 z-50">
          <button aria-label="Close menu overlay" className="absolute inset-0 bg-cash-ink/40" onClick={() => setMenuOpen(false)} />
          <div id="mobile-menu" ref={menuRef} role="dialog" aria-modal="true" aria-label="Site navigation"
            className="absolute right-0 top-0 h-full w-80 max-w-[85%] bg-white shadow-cash-md p-6 flex flex-col gap-2">
            <div className="flex items-center justify-between mb-2">
              <span className="font-bold text-cash-ink text-lg">Menu</span>
              <button className="h-10 w-10 inline-flex items-center justify-center rounded-full border border-cash-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1683C7]"
                aria-label="Close menu" onClick={() => { setMenuOpen(false); openerRef.current?.focus(); }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
              </button>
            </div>
            {navLinks.map((l) => (
              <a key={l.label} href={l.href} onClick={() => setMenuOpen(false)}
                className="py-3 px-2 rounded-lg text-cash-ink text-lg font-semibold hover:bg-cash-100">{l.label}</a>
            ))}
            <div className="mt-auto flex flex-col gap-3 pt-4 border-t border-cash-200">
              {user ? (
                <>
                  <div className="text-center text-sm text-cash-700">Signed in as <strong>{user.firstName}</strong></div>
                  <Link to="/account" onClick={() => setMenuOpen(false)} className={btnPrimary}>My account</Link>
                  <Link to="/shop" onClick={() => setMenuOpen(false)} className={btnGhost}>Browse Catalog</Link>
                  <button onClick={handleLogout} className="text-center font-semibold text-[#1683C7] py-2">Log out</button>
                </>
              ) : (
                <>
                  <Link to="/shop" onClick={() => setMenuOpen(false)} className={btnPrimary}>Browse Catalog</Link>
                  <Link to="/login" onClick={() => setMenuOpen(false)} className={btnGhost}>Log in</Link>
                  <Link to="/register" onClick={() => setMenuOpen(false)} className="text-center font-semibold text-[#1683C7] py-2">Create account</Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Hero — copy left, vial product shot right
// ──────────────────────────────────────────────────────────────────────────
function HeroSection() {
  // Campaign-aware hero: ?c=<slug> on the homepage link swaps this copy so each
  // marketing effort lands on a genuinely different homepage. Falls back to the
  // default copy when no campaign is set. See src/data/campaigns.js.
  const hero = getActiveHero();
  return (
    <section className="relative pt-10 md:pt-16 pb-16 md:pb-24">
      <div className="cash-container grid md:grid-cols-2 gap-10 md:gap-16 items-center">
        <div>
          <p className="cash-reveal is-revealed inline-flex items-center rounded-full border border-cash-200 bg-white px-3.5 py-1.5 text-[12px] font-semibold tracking-[0.12em] uppercase text-[#1683C7] shadow-sm">
            {hero.eyebrow}
          </p>
          <h1 className="cash-reveal is-revealed mt-6 font-bold tracking-tight text-cash-ink text-[44px] leading-[1.03] md:text-[64px] md:leading-[1.0]">
            {hero.headline[0]}<br />
            <span className="text-[#1FA7E8]">{hero.headline[1]}</span>
          </h1>
          <p className="cash-reveal is-revealed text-cash-700 text-[17px] md:text-[19px] mt-6 max-w-[500px] leading-relaxed" data-delay="1">
            {hero.sub}
          </p>
          <div className="cash-reveal is-revealed mt-8 flex flex-wrap items-center gap-3" data-delay="2">
            <Link to="/shop" className={btnPrimary}>Shop now <ArrowRight /></Link>
            <Link to="/coa" className={btnGhost}>View COAs</Link>
          </div>
          <p className="cash-reveal is-revealed mt-8 text-[13px] text-cash-500 max-w-[460px] leading-relaxed" data-delay="3">
            Sold for in-vitro laboratory research only, not for human or veterinary
            consumption. By purchasing, you confirm you are a qualified researcher
            or laboratory provider.
          </p>
        </div>

        <div className="cash-reveal is-revealed flex justify-center" data-delay="2">
          <div className={'relative w-full max-w-[420px] aspect-square ' + card + ' overflow-hidden flex items-center justify-center'}
            style={{ background: 'linear-gradient(160deg, #EAF6FF 0%, #F6FBFE 55%, #DCEFFB 100%)' }}>
            <VialPhoto slug="ghk-cu" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute z-20 top-4 left-4 rounded-xl bg-white/90 backdrop-blur-sm px-2.5 py-1.5 shadow-md rotate-[-4deg]">
              <div className="text-[8px] text-cash-500 font-bold tracking-widest uppercase">Purity</div>
              <div className="font-bold text-cash-ink text-base leading-none mt-0.5">≥ 99%</div>
              <div className="text-[9px] font-semibold mt-0.5 text-[#1683C7]">HPLC verified</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Trust grid — UNRL-style icon cards
// ──────────────────────────────────────────────────────────────────────────
const TRUST_ITEMS = [
  { label: 'Third-party tested', sub: 'COA on every lot', icon: <><path d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z" /><path d="M9 12l2 2 4-4.5" /></> },
  { label: '≥ 99% purity',       sub: 'HPLC verified',    icon: <><path d="M9 3h6M10 3v6l-5 9a2 2 0 0 0 1.8 3h10.4a2 2 0 0 0 1.8-3l-5-9V3" /></> },
  { label: 'USA based',          sub: 'Made in-house',    icon: <><path d="M12 22s-8-5-8-11a8 8 0 0 1 16 0c0 6-8 11-8 11z" /><circle cx="12" cy="11" r="3" /></> },
  { label: 'Discreet shipping',  sub: 'Tracked',          icon: <><path d="M3 7h11v9H3z" /><path d="M14 10h4l3 3v3h-7z" /><circle cx="7" cy="18" r="1.5" /><circle cx="17.5" cy="18" r="1.5" /></> },
  { label: 'Batch traceable',    sub: 'Lot # on every vial', icon: <path d="M7 3h7l5 5v13H7zM14 3v5h5M9 12h6M9 16h4" /> },
];

function TrustGrid() {
  return (
    <section className="pb-4">
      <div className="cash-container">
        <ul className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4">
          {TRUST_ITEMS.map((it) => (
            <li key={it.label} className={card + ' !shadow-[0_8px_24px_rgba(20,120,200,0.07)] flex items-center gap-3 px-4 py-3.5'}>
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#EAF6FF] text-[#1683C7]">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">{it.icon}</svg>
              </span>
              <span className="min-w-0">
                <span className="block font-bold text-cash-ink text-[13.5px] leading-tight">{it.label}</span>
                <span className="block text-cash-500 text-[12px] leading-tight mt-0.5">{it.sub}</span>
              </span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Feature cards — grid-textured cards, visual bleeding off the right edge
// ──────────────────────────────────────────────────────────────────────────
function Molecule() {
  return (
    <svg width="220" height="200" viewBox="0 0 220 200" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="mol" cx="38%" cy="32%" r="70%">
          <stop offset="0%" stopColor="#EAF8FF" /><stop offset="55%" stopColor="#8FD6F7" /><stop offset="100%" stopColor="#2EA9E8" />
        </radialGradient>
      </defs>
      <g stroke="#9FD6F2" strokeWidth="3" opacity="0.7">
        <line x1="70" y1="70" x2="120" y2="50" /><line x1="120" y1="50" x2="170" y2="78" />
        <line x1="70" y1="70" x2="80" y2="130" /><line x1="80" y1="130" x2="140" y2="150" />
        <line x1="140" y1="150" x2="170" y2="78" /><line x1="120" y1="50" x2="80" y2="130" />
      </g>
      {[[70,70,18],[120,50,15],[170,78,16],[80,130,17],[140,150,19]].map(([cx,cy,r],i)=>(
        <g key={i}>
          <circle cx={cx} cy={cy} r={r} fill="url(#mol)" />
          <ellipse cx={cx-r*0.3} cy={cy-r*0.35} rx={r*0.4} ry={r*0.28} fill="#ffffff" opacity="0.6" />
        </g>
      ))}
    </svg>
  );
}

// A row of real catalog photos, shown as small standing "shelf" chips.
function VialChip({ slug }) {
  return (
    <div className="h-24 w-16 md:h-28 md:w-[76px] rounded-xl overflow-hidden ring-1 ring-white shadow-[0_10px_24px_rgba(20,120,200,0.20)]">
      <img
        src={productImage(slug)}
        onError={onImageError}
        alt=""
        aria-hidden="true"
        loading="lazy"
        className="w-full h-full object-cover"
      />
    </div>
  );
}

const FEATURE_CARDS = [
  {
    head1: 'Lyophilized', head2: 'in the USA.', kicker: 'Made in-house',
    detail: 'Synthesized, lyophilized, and QC-tested at our US facility. No third-party contract manufacturing.',
    visual: (
      <div className="mb-7 h-32 w-32 md:h-44 md:w-44 rounded-2xl overflow-hidden ring-1 ring-white shadow-[0_14px_34px_rgba(20,120,200,0.22)]">
        <VialPhoto slug="bpc-157" className="w-full h-full object-cover" />
      </div>
    ),
  },
  {
    head1: 'No more', head2: 'guessing games.', kicker: 'US-made, third-party batch tested',
    detail: 'Every batch is independently tested for identity and purity before it ships to your lab.',
    visual: <Molecule />,
  },
  {
    head1: 'Batch produced,', head2: 'batch tested.', kicker: 'COA on every batch',
    detail: 'Certificates of analysis travel with every order. Scan the lot number to pull the full data sheet.',
    centerVisual: true,
    visual: (
      <div className="flex items-end gap-2.5">
        {['semaglutide', 'ghk-cu', 'nad-plus', 'glow', 'reta'].map((s) => (
          <VialChip key={s} slug={s} />
        ))}
      </div>
    ),
  },
];

function FeatureCards() {
  return (
    <section className="py-16 md:py-24">
      <div className="cash-container max-w-5xl mx-auto">
        <div className="max-w-2xl mb-10 md:mb-14">
          <h2 className="font-bold tracking-tight text-cash-ink text-[30px] md:text-[44px] leading-[1.06]">
            Built for labs that need to{' '}
            <span className="text-[#1FA7E8]">trust what's in the vial.</span>
          </h2>
        </div>

        <div className="flex flex-col gap-5">
          {FEATURE_CARDS.map((c, i) => (
            <div key={i} className={card + ' relative overflow-hidden p-8 md:p-10 min-h-[200px] md:min-h-[210px]'}>
              <div aria-hidden="true" className="absolute inset-0 opacity-90" style={gridTexture} />
              <div className="relative z-10 max-w-[58%] md:max-w-[55%]">
                <p className="text-[12.5px] font-semibold tracking-wide text-[#1683C7]">{c.kicker}</p>
                <h3 className="mt-2 font-bold text-cash-ink text-[24px] md:text-[30px] leading-[1.1] tracking-tight">
                  {c.head1}<br /><span className="text-[#1FA7E8]">{c.head2}</span>
                </h3>
                <p className="mt-4 text-cash-700 text-[15px] md:text-[16px] leading-relaxed max-w-[420px]">{c.detail}</p>
              </div>
              <div aria-hidden="true" className={'pointer-events-none absolute right-2 md:right-8 z-0 flex h-full opacity-95 ' + (c.centerVisual ? 'inset-y-0 items-center' : 'bottom-0 items-end')}>
                {c.visual}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Featured catalog — product tiles
// ──────────────────────────────────────────────────────────────────────────
const FEATURED = [
  { label: 'BPC-157',     product: '5 mg',   search: 'bpc-157',     slug: 'bpc-157' },
  { label: 'TB-500',      product: '5 mg',   search: 'tb-500',      slug: 'tb-500' },
  { label: 'Semaglutide', product: '5 mg',   search: 'semaglutide', slug: 'semaglutide' },
  { label: 'RETA',        product: '10 mg',  search: 'reta',        slug: 'reta' },
  { label: 'GHK-Cu',      product: '50 mg',  search: 'ghk',         slug: 'ghk-cu' },
  { label: 'NAD+',        product: '100 mg', search: 'nad',         slug: 'nad-plus' },
  { label: 'Glow',        product: 'Blend',  search: 'glow',        slug: 'glow' },
  { label: 'Wolverine',   product: 'Blend',  search: 'wolverine',   slug: 'wolverine' },
];

function FeaturedSection() {
  return (
    <section id="catalog" className="py-16 md:py-24">
      <div className="cash-container">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <h2 className="text-cash-ink font-bold tracking-tight text-[30px] md:text-[44px] leading-[1.06] max-w-xl">
            Popular research compounds.
          </h2>
          <Link to="/shop" className={btnGhost + ' self-start md:self-auto !py-3 !px-6 !text-[14px]'}>
            See full catalog <ArrowRight size={14} />
          </Link>
        </div>

        <ul className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {FEATURED.map((f) => (
            <li key={f.label}>
              <Link to={f.search ? `/shop?search=${f.search}` : '/shop'}
                className={'group block ' + card + ' p-4 transition-all hover:-translate-y-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1683C7]'}>
                <div className="relative h-44 rounded-2xl overflow-hidden">
                  <img
                    src={productImage(f.slug)}
                    onError={onImageError}
                    alt={f.label}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <h3 className="mt-4 text-cash-ink font-bold text-[17px]">{f.label}</h3>
                <p className="text-cash-500 text-[13px] mt-0.5">Research peptide · {f.product}</p>
                <span className="mt-3 inline-flex items-center gap-1 font-semibold text-[14px] text-[#1683C7]">
                  View product <span aria-hidden className="transition-transform group-hover:translate-x-1">→</span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Promise — light UNRL-style pillar cards
// ──────────────────────────────────────────────────────────────────────────
const PILLARS = [
  { key: 'truth', title: 'Truth', body: 'Every batch is identity- and purity-tested before it ships. The data, not a slogan, stands behind each product.', icon: <><path d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z" /><path d="M9 12l2 2 4-4.5" /></> },
  { key: 'transparency', title: 'Transparency', body: 'Certificates of analysis, sourcing and storage details are published up front. Read the same numbers our lab does.', icon: <><path d="M2.5 12s3.5-6.5 9.5-6.5S21.5 12 21.5 12s-3.5 6.5-9.5 6.5S2.5 12 2.5 12z" /><circle cx="12" cy="12" r="2.8" /></> },
  { key: 'shipping', title: 'Shipping', body: 'Orders ship from our US warehouse with tracking on every parcel. If a package is lost or damaged, we reship or refund.', icon: <><path d="M3 7h11v9H3z" /><path d="M14 10h4l3 3v3h-7z" /><circle cx="7" cy="18" r="1.8" /><circle cx="17.5" cy="18" r="1.8" /></> },
  { key: 'pricing', title: 'Pricing', body: 'The number you see is the number you pay. No hidden handling fees, no markups buried in shipping.', icon: <><path d="M12 4.5l7 7-7.5 7.5-7-7V4.5z" /><circle cx="9" cy="9" r="1.3" fill="currentColor" /></> },
];

function ShowcaseSection() {
  return (
    <section id="promise" className="py-16 md:py-24">
      <div className="cash-container">
        <div className="max-w-2xl mb-10 md:mb-14">
          <p className="font-semibold uppercase tracking-[0.18em] text-[12px] text-[#1683C7]">Our promise</p>
          <h2 className="text-cash-ink font-bold tracking-tight text-[30px] md:text-[44px] leading-[1.06] mt-2">
            Built on truth, <span className="text-[#1FA7E8]">shown in the open.</span>
          </h2>
        </div>
        <div className="grid gap-4 md:gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {PILLARS.map((p) => (
            <article key={p.key} className={card + ' p-7 transition-all hover:-translate-y-1'}>
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#EAF6FF] text-[#1683C7]">
                <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">{p.icon}</svg>
              </div>
              <h3 className="text-cash-ink font-bold text-[19px] mt-5">{p.title}</h3>
              <p className="text-cash-700 text-[14.5px] leading-relaxed mt-2">{p.body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// VIP membership band — sells the paid VIP plan (member pricing + free shipping)
// ──────────────────────────────────────────────────────────────────────────
const VIP_PERKS = [
  { t: 'VIP pricing on everything', d: 'The lowest member price on every peptide, applied automatically at checkout.' },
  { t: 'Free shipping, always', d: 'Every order ships free for the life of your membership — no minimums.' },
  { t: 'Locked-in savings', d: 'Your member rate holds even as retail prices rise.' },
  { t: 'Member-only drops', d: 'Early access to new compounds and VIP-only offers.' },
];

function VipBand() {
  return (
    <section id="vip" className="py-16 md:py-24">
      <div className="cash-container max-w-5xl mx-auto">
        <div className={'relative overflow-hidden ' + card}
          style={{ background: 'linear-gradient(150deg, #0D5E8A 0%, #1278AE 45%, #1FA7E8 100%)' }}>
          <div aria-hidden="true" className="absolute inset-0 opacity-20" style={gridTexture} />
          <div className="relative z-10 grid md:grid-cols-12 gap-8 p-8 md:p-14">
            <div className="md:col-span-7 text-white">
              <p className="inline-flex items-center rounded-full bg-white/15 px-3.5 py-1.5 text-[12px] font-semibold tracking-[0.14em] uppercase">
                Aloha Amino VIP
              </p>
              <h2 className="mt-5 font-bold tracking-tight text-[30px] md:text-[44px] leading-[1.05]">
                Pay the member price<br />
                <span className="text-tea-100">on every single vial.</span>
              </h2>
              <p className="mt-4 text-tea-50/90 text-[16px] md:text-[17px] leading-relaxed max-w-[480px]">
                One membership unlocks VIP pricing across the whole catalog plus free
                shipping on every order. From just <strong className="text-white">$5/month</strong>, billed annually.
              </p>
              <div className="mt-7 flex flex-wrap items-center gap-3">
                <Link to="/membership" className="inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 font-semibold text-[15px] bg-white text-[#1278AE] hover:bg-tea-50 shadow-lg transition-all hover:-translate-y-0.5">
                  Become a VIP <ArrowRight />
                </Link>
                <Link to="/membership" className="inline-flex items-center justify-center gap-2 rounded-full border border-white/40 px-7 py-3.5 font-semibold text-[15px] text-white hover:bg-white/10 transition-all">
                  See plans
                </Link>
              </div>
              <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-[13px] text-tea-50/80">
                <span><strong className="text-white">$60</strong>/year · best value</span>
                <span><strong className="text-white">$20</strong>/month · flexible</span>
                <span>Cancel anytime</span>
              </div>
            </div>

            <div className="md:col-span-5">
              <ul className="grid gap-3">
                {VIP_PERKS.map(p => (
                  <li key={p.t} className="rounded-2xl bg-white/10 backdrop-blur-sm px-4 py-3.5">
                    <div className="flex items-center gap-2 text-white font-bold text-[14px]">
                      <svg className="h-4 w-4 text-tea-100 shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M16.7 5.3a1 1 0 0 1 0 1.4l-7.5 7.5a1 1 0 0 1-1.4 0l-3.5-3.5a1 1 0 1 1 1.4-1.4l2.8 2.79 6.8-6.79a1 1 0 0 1 1.4 0z" clipRule="evenodd" />
                      </svg>
                      {p.t}
                    </div>
                    <p className="mt-1 ml-6 text-tea-50/80 text-[12.5px] leading-relaxed">{p.d}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// CTA band
// ──────────────────────────────────────────────────────────────────────────
function CtaBand() {
  return (
    <section className="py-16 md:py-24">
      <div className="cash-container max-w-5xl mx-auto">
        <div className={'relative overflow-hidden ' + card + ' grid md:grid-cols-12'}
          style={{ background: 'linear-gradient(150deg, #EAF6FF, #D7ECFB)' }}>
          <div className="md:col-span-8 p-8 md:p-14">
            <h2 className="text-cash-ink font-bold tracking-tight text-[28px] md:text-[42px] leading-[1.08]">
              Say Aloha to your <span className="text-[#1FA7E8]">new peptide website.</span>
            </h2>
            <p className="text-cash-700 text-lg mt-4 max-w-[560px] leading-relaxed">
              Create a free account to verify your lab credentials, then check out
              with tracked domestic shipping. Documentation travels with every order.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/shop" className={btnPrimary}>Browse Catalog <ArrowRight /></Link>
              <Link to="/register" className={btnGhost}>Create free account</Link>
            </div>
          </div>
          <div className="md:col-span-4 relative overflow-hidden min-h-[200px]">
            <VialPhoto slug="glow" className="absolute inset-0 w-full h-full object-cover" />
          </div>
        </div>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Testimonials — scroll-snap carousel
// ──────────────────────────────────────────────────────────────────────────
const TESTIMONIALS = [
  { name: 'Dr. Sarah M.', context: 'Research lab',  quote: 'The COA matched the vial to the milligram. That kind of consistency is exactly what I need to trust a supplier.' },
  { name: 'James K.',     context: 'Lab manager',   quote: 'Ordered Friday, on the bench Monday. Tracked the whole way, packed cold. No drama.' },
  { name: 'Priya L.',     context: 'Postdoc',       quote: 'Cleanest checkout I have used. Clear pricing, no surprise fees, total under a minute.' },
  { name: 'Marco D.',     context: 'Researcher',    quote: 'Purity numbers are published, not hand-waved. I can show my PI the data before we buy.' },
  { name: 'Aisha R.',     context: 'Lab director',  quote: 'COA on every batch, fast shipping, lab-provider verification. Finally a supplier I can standardize on.' },
];

function TestimonialsSection() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const trackRef = useRef(null);

  const next = useCallback(() => setIndex((i) => (i + 1) % TESTIMONIALS.length), []);
  const prev = useCallback(() => setIndex((i) => (i - 1 + TESTIMONIALS.length) % TESTIMONIALS.length), []);

  useEffect(() => {
    if (paused) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const t = setInterval(next, 5000);
    return () => clearInterval(t);
  }, [paused, next]);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const c = el.children[index];
    if (c) el.scrollTo({ left: c.offsetLeft - el.offsetLeft, behavior: 'smooth' });
  }, [index]);

  const onKeyDown = (e) => {
    if (e.key === 'ArrowRight') { e.preventDefault(); next(); }
    if (e.key === 'ArrowLeft')  { e.preventDefault(); prev(); }
  };

  return (
    <section id="support" className="py-16 md:py-24"
      onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)} onBlur={() => setPaused(false)}>
      <div className="cash-container">
        <div className="flex items-end justify-between gap-6 flex-wrap mb-10">
          <h2 className="text-cash-ink font-bold tracking-tight text-[28px] md:text-[40px] leading-tight max-w-xl">
            Trusted by research labs.
          </h2>
          <div className="flex items-center gap-2" role="group" aria-label="Testimonial controls">
            <button onClick={prev} aria-label="Previous testimonial"
              className="h-11 w-11 rounded-full border border-cash-200 bg-white text-cash-ink hover:border-[#1FA7E8] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1683C7]">‹</button>
            <button onClick={next} aria-label="Next testimonial"
              className="h-11 w-11 rounded-full border border-cash-200 bg-white text-cash-ink hover:border-[#1FA7E8] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1683C7]">›</button>
          </div>
        </div>

        <div ref={trackRef} tabIndex={0} role="region" aria-roledescription="carousel" aria-label="Customer testimonials" aria-live="polite" onKeyDown={onKeyDown}
          className="flex gap-5 overflow-x-auto pb-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1683C7] rounded-2xl"
          style={{ scrollSnapType: 'x mandatory', scrollbarWidth: 'none' }}>
          {TESTIMONIALS.map((t, i) => (
            <figure key={t.name} role="group" aria-roledescription="slide" aria-label={`${i + 1} of ${TESTIMONIALS.length}`}
              className={'flex-shrink-0 ' + card + ' p-7 w-[85%] sm:w-[60%] md:w-[calc((100%-2.5rem)/3)] ' + (i === index ? 'ring-1 ring-[#9FD2F2]' : '')}
              style={{ scrollSnapAlign: 'start' }}>
              <blockquote className="text-cash-ink text-lg leading-snug font-medium">“{t.quote}”</blockquote>
              <figcaption className="mt-6 flex items-center gap-3">
                <span aria-hidden="true" className="h-10 w-10 rounded-full bg-[#EAF6FF] inline-flex items-center justify-center font-bold text-sm text-[#1683C7]">{t.name.charAt(0)}</span>
                <span>
                  <span className="block font-semibold text-cash-ink">{t.name}</span>
                  <span className="block text-cash-500 text-sm">{t.context}</span>
                </span>
              </figcaption>
            </figure>
          ))}
        </div>

        <div className="mt-5 flex justify-center gap-2" aria-hidden="true">
          {TESTIMONIALS.map((_, i) => (
            <span key={i} className="h-1.5 rounded-full transition-all duration-300"
              style={{ width: i === index ? '24px' : '6px', background: i === index ? BRIGHT : '#bcdcf5' }} />
          ))}
        </div>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Footer
// ──────────────────────────────────────────────────────────────────────────
function HomeFooter() {
  const cols = [
    { heading: 'Catalog', links: [
      { to: '/shop', label: 'All peptides' }, { to: '/shop?search=bpc', label: 'BPC-157' },
      { to: '/membership', label: 'VIP membership' }, { to: '/account', label: 'My account' },
    ]},
    { heading: 'Company', links: [
      { to: '/about', label: 'About' }, { to: '/support', label: 'Lab support' }, { to: '/coa', label: 'COA library' },
    ]},
    { heading: 'Legal', links: [
      { to: '/terms', label: 'Terms' }, { to: '/privacy', label: 'Privacy' }, { to: '/compliance', label: 'Research use disclosure' },
    ]},
  ];

  const SocialIcon = ({ label, d }) => (
    <button type="button" aria-label={label}
       className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-cash-200 text-cash-700 hover:border-[#1FA7E8] hover:text-[#1683C7] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1683C7]">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d={d} /></svg>
    </button>
  );

  return (
    <footer className="bg-white/70 border-t border-cash-200 pt-16 pb-10 mt-4">
      <div className="cash-container">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="flex items-center">
              <Logo className="text-2xl" />
            </div>
            <p className="text-cash-700 mt-4 max-w-sm leading-relaxed">
              Research-grade peptides for licensed laboratories. Identity- and
              purity-tested, shipped with batch-matched documentation.
            </p>
            <div className="mt-6 flex gap-3" aria-label="Social media">
              <SocialIcon label="Instagram"  d="M12 2.2c3.2 0 3.6 0 4.85.07 1.17.06 1.95.24 2.4.4.6.24 1.05.52 1.5.97s.73.9.97 1.5c.16.45.34 1.23.4 2.4.07 1.25.07 1.65.07 4.85s0 3.6-.07 4.85c-.06 1.17-.24 1.95-.4 2.4a3.7 3.7 0 0 1-.97 1.5 3.7 3.7 0 0 1-1.5.97c-.45.16-1.23.34-2.4.4-1.25.07-1.65.07-4.85.07s-3.6 0-4.85-.07c-1.17-.06-1.95-.24-2.4-.4a3.7 3.7 0 0 1-1.5-.97 3.7 3.7 0 0 1-.97-1.5c-.16-.45-.34-1.23-.4-2.4C2.2 15.6 2.2 15.2 2.2 12s0-3.6.07-4.85c.06-1.17.24-1.95.4-2.4.24-.6.52-1.05.97-1.5s.9-.73 1.5-.97c.45-.16 1.23-.34 2.4-.4C8.4 2.2 8.8 2.2 12 2.2zm0 5.3a4.5 4.5 0 1 0 0 9 4.5 4.5 0 0 0 0-9zm0 7.4a2.9 2.9 0 1 1 0-5.8 2.9 2.9 0 0 1 0 5.8zm5.85-7.6a1.05 1.05 0 1 1-2.1 0 1.05 1.05 0 0 1 2.1 0z" />
              <SocialIcon label="X (Twitter)" d="M18.244 2H21l-6.52 7.45L22 22h-6.79l-4.78-6.25L4.96 22H2.2l6.98-7.97L2 2h6.93l4.32 5.7L18.244 2zm-2.38 18h1.66L8.22 4H6.46l9.404 16z" />
              <SocialIcon label="TikTok"     d="M21 8.5a7.4 7.4 0 0 1-4.5-1.55v8.2a6.25 6.25 0 1 1-6.25-6.25c.3 0 .6.02.9.07v3.05a3.25 3.25 0 1 0 2.35 3.13V2h2.95A4.46 4.46 0 0 0 21 5.5v3z" />
            </div>
          </div>

          {cols.map((c) => (
            <div key={c.heading} className="md:col-span-2">
              <h4 className="font-bold text-cash-ink mb-4">{c.heading}</h4>
              <ul className="space-y-3 text-cash-700 text-[15px]">
                {c.links.map((l) => (
                  <li key={l.label}>
                    <Link to={l.to} className="hover:text-[#1683C7] transition-colors focus:outline-none focus-visible:underline">{l.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <div className="md:col-span-1" />
        </div>

        <div className="mt-14 pt-6 border-t border-cash-200 flex flex-col md:flex-row md:items-center justify-between gap-4 text-cash-500 text-[13px]">
          <p>© {new Date().getFullYear()} Aloha Amino Research Inc. All rights reserved.</p>
          <p className="max-w-xl">
            FOR LABORATORY RESEARCH USE ONLY. Products are sold for in-vitro
            research and laboratory use; they are not drugs, foods, or cosmetics,
            and have not been approved by the FDA for human or veterinary use.
            Customers represent that they are qualified researchers or laboratory providers.
          </p>
        </div>
      </div>
    </footer>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Page
// ──────────────────────────────────────────────────────────────────────────
export default function LandingPage() {
  const { user } = useAuthCtx();
  const idle = useIdleTrigger(120_000);

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      document.querySelectorAll('.cash-reveal').forEach((el) => el.classList.add('is-revealed'));
    });
    return () => cancelAnimationFrame(id);
  }, []);

  return (
    <div className="min-h-screen flex flex-col text-cash-ink"
      style={{ background: 'linear-gradient(180deg, #E6F2FB 0%, #F3F9FE 28%, #EAF5FC 62%, #F4FAFE 100%)' }}>
      <a href="#main" className="cash-skip-link">Skip to content</a>
      <HomeHeader />
      <main id="main" tabIndex={-1}>
        <HeroSection />
        <TrustGrid />
        <FeatureCards />
        <FeaturedSection />
        <VipBand />
        <ShowcaseSection />
        <CtaBand />
        <TestimonialsSection />
      </main>
      <HomeFooter />
      {!user && <SignupPromoModal open={idle} />}
    </div>
  );
}
